import { useCallback, useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import classnames from 'classnames/bind';
import { scaleLinear } from 'd3-scale';
import { select, selectAll } from 'd3-selection';
import { isFinite } from 'lodash';
import { useUnmount } from 'react-use';
import { conditionClassModifier } from '@surfline/web-common';
import { findClosestTimestamp } from 'utils/time';

import type { ConditionRating } from 'types/conditionRating';
import type { LiveWindPoint } from 'types/liveWind';
import type { Units } from 'types/units';
import type { WindTimestep } from 'types/wind';
import scaleWindSpeed from 'utils/scaleWindSpeed';

import styles from './WindForecastGraphContainer.module.scss';

const cx = classnames.bind(styles);

const getBarClassNames = ({
  className,
  condition,
  isActive,
}: {
  className: string;
  condition?: string;
  isActive: boolean;
}) =>
  cx({
    [`${className}--forecastGraph`]: true,
    [`${className}`]: true,
    highlighted: className === 'highlightedBarGraphConditionBar',
    [`sl-conditions-color--${conditionClassModifier(condition)}-fill`]:
      isActive && !!condition && !(className === 'highlightedBarGraphConditionBar'),
    active: isActive,
  });

const getVerticalLineClassNames = ({
  isActive,
  isCurrentTimeHour,
}: {
  isActive: boolean;
  isCurrentTimeHour: boolean;
}) =>
  cx('verticalLine', {
    'verticalLine--active': isActive,
    'verticalLine--currentHour': isCurrentTimeHour,
  });

const useSetupWindBarGraphCanvas = ({
  windData,
  maxWindSpeed,
  units,
  activeItem,
  graphWidth,
  day = 0,
  displayOrderIndex = 0,
  isMobile = false,
}: {
  windData: Array<LiveWindPoint | WindTimestep>;
  maxWindSpeed: number;
  units: Units;
  activeItem: {
    day: number;
    hour: number;
    condition?: ConditionRating;
  };
  graphWidth?: number;
  day: number;
  displayOrderIndex: number;
  isMobile: boolean;
}) => {
  const [barWidth, setBarWidth] = useState<number>(0);
  const [barSpacing, setBarSpacing] = useState<number>(0);
  const theme = useTheme();

  const updateActiveBar = useCallback(() => {
    if (day === activeItem?.day && isFinite(activeItem?.hour)) {
      // Update bar styles
      selectAll(`#wind-bar-graph-canvas-day-${day} .barGraphConditionBar`).attr('class', (_, i) =>
        getBarClassNames({
          className:
            day === activeItem?.day && i === activeItem?.hour
              ? 'highlightedBarGraphConditionBar'
              : 'barGraphConditionBar',
          condition: activeItem?.condition?.rating || 'none',
          isActive: i === activeItem?.hour,
        }),
      );

      // Update vertical line styles
      selectAll<SVGLineElement, LiveWindPoint | WindTimestep>(
        `#wind-bar-graph-canvas-day-${day} .verticalLine`,
      ).attr('class', (d, i) => {
        const timestamps = windData.map((point) => point.timestamp);
        const isCurrentTimeHour =
          day === 0 &&
          windData[i]?.timestamp === findClosestTimestamp(timestamps, Date.now() / 1000, true);
        const isObserved = day === 0 && 'wind' in d && d.wind?.speed != null;
        return cx(
          getVerticalLineClassNames({
            isActive: i === activeItem?.hour,
            isCurrentTimeHour,
          }),
          {
            windGraphVerticalBarObserved: isObserved,
          },
        );
      });
    }
  }, [activeItem?.condition?.rating, activeItem?.day, activeItem?.hour, day, windData]);

  useEffect(() => {
    updateActiveBar();
  }, [updateActiveBar]);

  const setUpBarGraph = useCallback(() => {
    if (graphWidth && isFinite(graphWidth) && windData) {
      select(`#wind-bar-graph-canvas-day-${day}`).selectChildren().remove();
      const GRAPH_HEIGHT = 70;
      const GRAPH_TOP_PADDING = 20;
      const HIDE_LOWER_BAR_RADIUS_OFFSET = 5;
      const CORNER_RADIUS = 3;

      const resize = scaleLinear().domain([2, 16]).range([1, 10]);
      const numBars = windData.length;
      const groupWidth = graphWidth / numBars;
      setBarWidth(resize(groupWidth));
      setBarSpacing((graphWidth - numBars * barWidth) / (windData.length - 1));

      const xScale = scaleLinear()
        .range([0, graphWidth - groupWidth])
        .domain([0, numBars - 1]);

      const canvas = select<HTMLDivElement, undefined>(`#wind-bar-graph-canvas-day-${day}`)
        .append('svg')
        .attr('height', GRAPH_HEIGHT)
        .attr('width', '100%')
        .attr('class', `barGraphSVG${day !== null ? `-${day}` : ``}`);

      // Create a clipPath to hide the rounded bottom of bars
      const defs = canvas.append('defs');
      defs
        .append('clipPath')
        .attr('id', `wind-graph-clip-path-${day}`)
        .append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', graphWidth)
        .attr('height', GRAPH_HEIGHT - CORNER_RADIUS)
        .attr('transform', `translate(0, 0)`);

      if (!isMobile && displayOrderIndex !== 0) {
        canvas
          .append('line')
          .attr('x1', 0)
          .attr('y1', 0)
          .attr('x2', 1)
          .attr('y2', GRAPH_HEIGHT)
          .style('stroke-width', 1)
          .style('stroke', theme.palette.grey[400])
          .style('fill', theme.palette.grey[400]);
      }

      const barHeight = (reading: LiveWindPoint | WindTimestep) =>
        Math.abs(
          Number(
            scaleWindSpeed(
              maxWindSpeed,
              (reading as LiveWindPoint)?.wind?.speed || (reading as WindTimestep)?.speed || 0,
              units as unknown as string,
              GRAPH_HEIGHT - GRAPH_TOP_PADDING,
            ).toFixed(10),
          ),
        ) +
        HIDE_LOWER_BAR_RADIUS_OFFSET +
        2;

      const barYPosition = (reading: LiveWindPoint | WindTimestep) => {
        const barTopPosition = barHeight(reading);
        return GRAPH_HEIGHT - barTopPosition + HIDE_LOWER_BAR_RADIUS_OFFSET;
      };

      // Create bar groups
      const barGroups = canvas
        .selectAll<SVGGElement, LiveWindPoint | WindTimestep>('g.bar-group')
        .data(windData)
        .enter()
        .append('g')
        .attr('class', 'bar-group')
        .attr('transform', (d, i) => `translate(${xScale(i)},${barYPosition(d)})`);

      // Add bars to groups
      barGroups
        .append('rect')
        .attr('width', barWidth)
        .attr('x', groupWidth / 2 - barWidth / 2)
        .attr('height', (d) => barHeight(d) - 1)
        .attr('rx', CORNER_RADIUS)
        .attr('ry', CORNER_RADIUS)
        .attr('clip-path', `url(#wind-graph-clip-path-${day})`)
        .attr('class', (_, i) =>
          getBarClassNames({
            className: 'barGraphConditionBar',
            condition: activeItem?.condition?.rating,
            isActive: day === activeItem?.day && i === activeItem?.hour,
          }),
        )
        .attr('data-testid', 'wind-graph-bar');

      // Add vertical lines
      barGroups
        .append('line')
        .attr('class', (d, i) => {
          const timestamps = windData.map((point) => point.timestamp);
          const isCurrentTimeHour =
            day === 0 &&
            windData[i]?.timestamp === findClosestTimestamp(timestamps, Date.now() / 1000, true);
          const isObserved = day === 0 && 'wind' in d && d.wind?.speed != null;
          return cx(
            getVerticalLineClassNames({
              isActive: day === activeItem?.day && i === activeItem?.hour,
              isCurrentTimeHour,
            }),
            {
              windGraphVerticalBarObserved: isObserved,
            },
          );
        })
        .attr('x1', groupWidth / 2)
        .attr('x2', groupWidth / 2)
        .attr('y1', -GRAPH_HEIGHT)
        .attr('y2', -1);

      updateActiveBar();
    }
  }, [
    activeItem?.condition?.rating,
    activeItem?.day,
    activeItem?.hour,
    barWidth,
    day,
    displayOrderIndex,
    graphWidth,
    isMobile,
    maxWindSpeed,
    theme.palette.grey,
    units,
    updateActiveBar,
    windData,
  ]);

  useUnmount(() => {
    select(`#wind-bar-graph-canvas-day-${day}`).selectChildren().remove();
    select(`#wind-bar-graph-canvas-day-${day}`).remove();
  });

  return { setUpBarGraph, barWidth, barSpacing };
};

export default useSetupWindBarGraphCanvas;
